import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import CommonSelect from "../../../../../core/common/commonSelect";
import { all_routes } from "../../../../router/all_routes";
import Cookies from "js-cookie";
import { useUserPermissions } from "../../../../UserPermissionsContext";

interface ErrorMessages {
  name?: string[];
  category?: string[];
  has_call_back_time?: string[];
  non_field_errors?: string[];
}

interface SelectOption {
  value: string;
  label: string;
}

const AddSedSubCategory = () => {
  const routes = all_routes;
  const navigate = useNavigate();
  const jwtToken = Cookies.get("authToken");
  const { webApi } = useUserPermissions();

  const [isLoading, setIsLoading] = useState(false);
  const [categories, setCategories] = useState<SelectOption[]>([]);
  const [selectedCategory, setSelectedCategory] = useState<SelectOption | null>(
    null,
  );
  const [errorMessage, setErrorMessage] = useState<ErrorMessages>({});

  const [formData, setFormData] = useState({
    name: "",
    category: "",
    has_call_back_time: false,
  });

  const headers = {
    "Content-Type": "application/json",
    Authorization: `Bearer ${jwtToken}`,
  };

  // Fetch Categories
  const fetchCategoriesData = async () => {
    try {
      const response = await fetch(
        `${webApi}/sed/sed_ticket_category_dropdown_for_subcategory_master/`,
        {
          method: "GET",
          headers,
        },
      );

      const result = await response.json();

      if (!response.ok) {
        if (result.code === "token_not_valid") {
          navigate("/academic/sri-chaitanya/crm/landing-page");
          return;
        }
        throw new Error(result.detail || "Failed to fetch categories");
      }

      const transformedCategories = result.map(
        (item: { id: number; name: string }) => ({
          value: String(item.id),
          label: item.name,
        }),
      );

      setCategories(transformedCategories);
    } catch (error) {
      console.error("Category fetch error:", error);
    }
  };

  useEffect(() => {
    fetchCategoriesData();
  }, []);

  const handleCategoryChange = (selected: SelectOption | null) => {
    setSelectedCategory(selected);

    setFormData((prev) => ({
      ...prev,
      category: selected?.value || "",
    }));

    if (errorMessage.category) {
      setErrorMessage((prev) => ({ ...prev, category: undefined }));
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData((prev) => ({
      ...prev,
      name: e.target.value,
    }));

    if (errorMessage.name) {
      setErrorMessage((prev) => ({ ...prev, name: undefined }));
    }
  };

  const updateData = async () => {
    const response = await fetch(`${webApi}/sed/sed_ticket_subcategory/`, {
      method: "POST",
      headers,
      body: JSON.stringify({
        name: formData.name,
        category: Number(formData.category),
        has_call_back_time: formData.has_call_back_time,
      }),
    });
    const result = await response.json();
    if (!response.ok) {
      if (result.code === "token_not_valid") {
        navigate("/academic/sri-chaitanya/crm/landing-page");
        return;
      }
      setErrorMessage(result);
      throw new Error(result.detail || "Failed");
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      await updateData();
      navigate(routes.SEDSubCategory);
    } catch (error) {
      console.error("Submit error:", error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div>
      <div className="page-wrapper">
        <div className="content container-fluid">
          <div className="page-header">
            <div className="row">
              <div className="col-sm-12">
                <h3 className="page-title">SED Add Sub Category</h3>
                <ul className="breadcrumb">
                  <li className="breadcrumb-item">
                    <Link to={routes.SEDDashBoard}>Dashboard</Link>
                  </li>
                  <li className="breadcrumb-item">
                    <Link to={routes.SEDSubCategory}>Sub Categories</Link>
                  </li>
                  <li className="breadcrumb-item active">Add Sub Category</li>
                </ul>
              </div>
            </div>
          </div>

          <div className="row justify-content-center">
            <div className="col-sm-12 d-flex justify-content-center">
              <div
                className="card"
                style={{
                  width: "70%",
                  boxShadow: "0 4px 8px 0 rgba(0,0,0,0.2)",
                  borderRadius: "10px",
                }}
              >
                <div className="card-body">
                  <form onSubmit={handleSubmit}>
                    {/* Category */}
                    <div className="mb-3 me-3" style={{ width: "35%" }}>
                      <label className="form-label">Category</label>
                      <CommonSelect
                        className="select"
                        options={categories}
                        onChange={handleCategoryChange}
                        value={selectedCategory}
                        isRequired={true}
                      />
                      {errorMessage.category && (
                        <small className="text-danger">
                          {errorMessage.category[0]}
                        </small>
                      )}
                    </div>

                    {/* Name */}
                    <div className="form-group mb-3" style={{ width: "35%" }}>
                      <label className="form-label">Sub Category Name</label>
                      <input
                        type="text"
                        className={`form-control ${
                          errorMessage.name ? "is-invalid" : ""
                        }`}
                        name="name"
                        placeholder="Sub Category Name"
                        onChange={handleInputChange}
                        value={formData.name}
                        required
                      />
                      {errorMessage.name && (
                        <div className="invalid-feedback">
                          {errorMessage.name[0]}
                        </div>
                      )}
                      {errorMessage.non_field_errors && (
                        <div className="invalid-feedback">
                          {errorMessage.non_field_errors[0]}
                        </div>
                      )}
                    </div>

                    {/* Callback */}
                    <div className="form-check mb-4">
                      <input
                        type="checkbox"
                        className="form-check-input"
                        checked={formData.has_call_back_time}
                        onChange={(e) =>
                          setFormData((prev) => ({
                            ...prev,
                            has_call_back_time: e.target.checked,
                          }))
                        }
                      />
                      <label className="form-check-label">
                        Has Callback Time
                      </label>
                    </div>

                    <div className="modal-footer">
                      <Link
                        className="btn btn-primary"
                        to={routes.SEDSubCategory}
                        style={{
                          backgroundColor: "lightcoral",
                          color: "white",
                          marginRight: "5px",
                        }}
                      >
                        Cancel
                      </Link>

                      <button
                        className="btn btn-primary"
                        type="submit"
                        disabled={isLoading}
                      >
                        {isLoading ? (
                          <i className="spinner-border spinner-border-sm" />
                        ) : (
                          "Save"
                        )}
                      </button>
                    </div>
                  </form>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddSedSubCategory;
